import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getBusinessPartnerPhone,
	getCashbackLedger,
	getReconciliationLines,
	getReconciliations,
	getSalesEmployees,
	getWarehouses,
} from './api'
import {
	INVOICE_SRC_OBJ_TYPE,
	belongsToSale,
	firstPhone,
	sumAmount,
} from './cashback'
import { sapApi } from './sapApi'
import { getApiErrorMessage } from './utils'

const cache = {}
const inflight = {}

const useCachedList = (key, loader) => {
	const [data, setData] = useState(cache[key] || [])
	const [loading, setLoading] = useState(!cache[key])
	const [error, setError] = useState('')

	useEffect(() => {
		let alive = true
		if (cache[key]) return

		if (!inflight[key]) inflight[key] = loader()

		inflight[key]
			.then((list) => {
				cache[key] = list
				if (alive) setData(list)
			})
			.catch((err) => {
				inflight[key] = null
				if (alive) setError(getApiErrorMessage(err))
			})
			.finally(() => {
				if (alive) setLoading(false)
			})

		return () => {
			alive = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key])

	return { data, loading, error }
}

export const useWarehouses = () =>
	useCachedList('warehouses', () => getWarehouses({ pageSize: 200 }))

export const useSalesEmployees = () =>
	useCachedList('salesEmployees', () => getSalesEmployees({ pageSize: 500 }))

// Eskisidagi «Кэшбэк по продаже» bloki: leder — haqiqat manbasi (saleId = docEntry),
// sverka esa provodka hujjatni yopganini tasdiqlaydi. Kesh-bek servisi ixtiyoriy —
// u javob bermasa ham hujjat kartochkasi ochilaverishi kerak.
export const useSaleCashback = ({ docEntry, phone, cardCode }) => {
	const [entries, setEntries] = useState([])
	const [loading, setLoading] = useState(false)
	const [reconciliations, setReconciliations] = useState([])
	const [reconciliationLines, setReconciliationLines] = useState({})
	const [reconciliationsLoading, setReconciliationsLoading] = useState(false)
	const [reloadKey, setReloadKey] = useState(0)

	const reload = useCallback(() => setReloadKey((prev) => prev + 1), [])

	useEffect(() => {
		if (!docEntry) return undefined
		let alive = true
		setLoading(true)

		const resolvePhone = () => {
			const known = firstPhone(phone)
			if (known) return Promise.resolve(known)
			if (!cardCode) return Promise.resolve('')
			// Telefon hujjatda yo'q — mijoz kartochkasidan olamiz
			return getBusinessPartnerPhone(cardCode).then(firstPhone).catch(() => '')
		}

		resolvePhone()
			.then((resolved) =>
				resolved
					? getCashbackLedger({ phone: resolved, page: 1, pageSize: 200 })
					: [],
			)
			.then((items) => {
				if (alive) setEntries(items.filter((item) => belongsToSale(item, docEntry)))
			})
			.catch(() => {
				if (alive) setEntries([])
			})
			.finally(() => {
				if (alive) setLoading(false)
			})

		return () => {
			alive = false
		}
	}, [docEntry, phone, cardCode, reloadKey])

	useEffect(() => {
		if (!docEntry || !cardCode) return undefined
		let alive = true
		setReconciliationsLoading(true)

		getReconciliations({
			cardCode,
			srcObjType: INVOICE_SRC_OBJ_TYPE,
			srcObjEntry: docEntry,
			pageSize: 50,
			$skip: 0,
		})
			.then((items) => {
				if (!alive) return []
				setReconciliations(items)
				const reconNums = [
					...new Set(items.map((item) => item?.reconNum).filter(Boolean)),
				]
				return Promise.all(
					reconNums.map((reconNum) =>
						getReconciliationLines(reconNum)
							.then((rows) => [reconNum, rows])
							.catch(() => [reconNum, []]),
					),
				)
			})
			.then((pairs) => {
				if (alive) setReconciliationLines(Object.fromEntries(pairs || []))
			})
			.catch(() => {
				if (!alive) return
				setReconciliations([])
				setReconciliationLines({})
			})
			.finally(() => {
				if (alive) setReconciliationsLoading(false)
			})

		return () => {
			alive = false
		}
	}, [docEntry, cardCode, reloadKey])

	const redeemed = useMemo(
		() => entries.filter((entry) => entry?.type === 'REDEEM'),
		[entries],
	)
	const earned = useMemo(
		() => entries.filter((entry) => entry?.type === 'EARN'),
		[entries],
	)

	return {
		entries,
		earned,
		redeemed,
		redeemedTotal: sumAmount(redeemed),
		earnedTotal: sumAmount(earned),
		currency: entries[0]?.currency || '',
		loading,
		reconciliations,
		reconciliationLines,
		reconciliationsLoading,
		reload,
	}
}

// Nasiya tariflari — eskisidagi CreateProducts kabi SAP dagi UDT lardan
// (`U_INSTCONFBASIC` — oddiy, `U_INSTCONFVIP` — B2B). Kesh-bek API sida bu
// ro'yxat yo'q; so'rov yiqilsa `constants.js` dagi qat'iy ro'yxatga qaytamiz.
const parsePlanRows = (res) => {
	const body = typeof res?.data === 'string' ? JSON.parse(res.data) : res?.data
	return (body?.value || [])
		.map((row) => ({
			months: Number(row.U_Months),
			percent: Number(row.U_Percent || 0),
		}))
		.filter((row) => row.months > 0)
		.sort((a, b) => a.months - b.months)
}

const loadPlans = (table) => sapApi.get(table).then(parsePlanRows)

export const useInstallmentPlans = () => {
	const basic = useCachedList('instconf-basic', () =>
		loadPlans('U_INSTCONFBASIC'),
	)
	const vip = useCachedList('instconf-vip', () => loadPlans('U_INSTCONFVIP'))

	return {
		basic: basic.data,
		vip: vip.data,
		loading: basic.loading || vip.loading,
		// SAP sessiyasi yo'q bo'lsa ham forma ishlashi kerak
		unavailable: Boolean(basic.error && vip.error),
	}
}
